import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  type IntakeAgreementState,
  intakeAgreementWaitingMessage,
} from '../../hooks/useIntakeAgreement';

export function IntakeAgreementPanel({
  agreement,
  staff = false,
  onSign,
  opening = false,
  signingFailed = false,
  confirming = false,
  practiceAdministrator = false,
}: {
  agreement: IntakeAgreementState;
  staff?: boolean;
  onSign?: () => void;
  opening?: boolean;
  signingFailed?: boolean;
  confirming?: boolean;
  /** Presentation only; signing authorization comes from the server's can_sign. */
  practiceAdministrator?: boolean;
}): React.JSX.Element {
  const { status, pending, failed, refresh } = agreement;
  const waitingOnAdmin =
    !staff &&
    !failed &&
    status?.requires_agreement &&
    !status.customer_completed &&
    status.availability === 'available' &&
    !status.unavailable_reason &&
    !status.can_sign;
  const canSign =
    !staff &&
    !failed &&
    status?.requires_agreement &&
    !status.customer_completed &&
    status.availability === 'available' &&
    !status.unavailable_reason &&
    status.can_sign;
  const message =
    confirming && !failed && !status?.customer_completed
      ? 'Checking signature confirmation…'
      : failed
        ? 'Agreement status is temporarily unavailable. You can continue your intake.'
        : !status
          ? 'Checking practice agreements…'
          : status.customer_completed
            ? 'Your practice administrator has signed the required agreements. Any remaining JotPsych signatures are handled separately.'
            : status.unavailable_reason === 'requires_support'
              ? 'Your agreements need assistance. Contact support for help with signing. You can continue your intake.'
              : status.unavailable_reason === 'preparing'
                ? 'Your agreements are being prepared. You can continue your intake in the meantime.'
                : status.availability !== 'available' || status.unavailable_reason
                  ? 'Your agreements are not ready yet. You can continue your intake and return here to sign.'
                  : staff
                    ? 'The designated practice administrator needs to sign the agreements.'
                    : !status.can_sign
                      ? intakeAgreementWaitingMessage(practiceAdministrator)
                      : 'Review and sign your practice agreements. You can also complete your intake while signatures are pending.';
  const refreshLabel = pending ? 'Checking agreement status…' : 'Refresh agreement status';
  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography component="h2" variant="h6">
          {waitingOnAdmin ? 'Waiting for your practice administrator' : 'Practice agreements'}
        </Typography>
        {staff && (
          <Tooltip title={refreshLabel}>
            <span>
              <IconButton
                size="small"
                aria-label={refreshLabel}
                disabled={pending || opening}
                onClick={() => void refresh()}
              >
                {pending ? (
                  <CircularProgress size={20} color="inherit" aria-hidden="true" />
                ) : (
                  <RefreshOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
      <Alert severity={failed || signingFailed ? 'error' : 'info'}>
        {opening ? 'Preparing your agreements…' : message}
      </Alert>
      {signingFailed && (
        <Alert severity="error">
          Signing could not be opened. Please try again. You can continue your intake in the
          meantime.
        </Alert>
      )}
      {canSign && onSign && (
        <Stack direction={{ xs: 'column', sm: 'row' }}>
          <Button variant="contained" disabled={pending || opening} onClick={onSign}>
            {opening ? 'Preparing your agreements…' : 'Review and sign agreements'}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
